import { cn } from "@/utils/cn";
import { Badge, Radio, RadioGroupProps, Tooltip } from "@mantine/core";
import { useClickOutside, useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconInfoCircle } from "@tabler/icons-react";
import { useMemo, useState } from "react";

type InputRadioGroupProps = Omit<RadioGroupProps, "children" | "onChange"> & {
  options: {
    value: string | number;
    label: string;
    disabled?: boolean;
    tooltipText?: string;
  }[];
  onChange: (value: any) => void;
  exceedsShippingLimits?: boolean;
  hasMultipleNonRollableItems?: boolean;
  isTooltipEnabled?: boolean;
};

const radioStyles = {
  radio: { height: "18px", width: "18px", cursor: "pointer" },
  icon: { height: "14px", width: "14px", transform: "unset", inset: "2px" },
  body: { display: "flex", alignItems: "center", justifyContent: "start" },
  inner: { height: "18px", width: "18px" },
  label: {
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: "24px",
    color: "#191919",
  },
};

const OVERSIZED_VALUE = "oversized";
const MULTIPLE_CALCULATED_VALUE = "multiple-calculated";

const InputRadioGroup = ({ options = [], ...props }: InputRadioGroupProps) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const pickupOption = options.find(o => o.value === "pickup");
  const [hovered, setHovered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const clickOutsideRef = useClickOutside(() => {
    setDismissed(true);
    setHovered(false);
  });

  const displayOptions = useMemo(
    () =>
      props.exceedsShippingLimits
        ? [
            {
              value: OVERSIZED_VALUE,
              // 2026-10-22 (T-51664_B): say up front that the messenger
              // exists — the old badge read as "we can't ship this".
              label: isMobile
                ? "Too large for UPS"
                : "Too large for UPS — messenger delivery available in NYC",
              tooltipText:
                "Your piece exceeds UPS's box limit. In NYC a priced messenger delivery is offered once you enter the address; elsewhere the studio will contact you with a delivery quote after your order is placed.",
            },
            ...(pickupOption ? [pickupOption] : []),
          ]
        : props.hasMultipleNonRollableItems
          ? [
              {
                value: MULTIPLE_CALCULATED_VALUE,
                label: isMobile
                  ? "Multiple items"
                  : "Multiple items — messenger in NYC, otherwise quoted",
                tooltipText:
                  "UPS can't price this order automatically. In NYC a priced messenger delivery is offered once you enter the address; elsewhere the studio will contact you with a shipping quote after your order is placed.",
              },
              ...(pickupOption ? [pickupOption] : []),
            ]
          : options,
    [
      props.exceedsShippingLimits,
      props.hasMultipleNonRollableItems,
      pickupOption,
      options,
    ]
  );

  return (
    <Radio.Group {...props} className="*:flex *:flex-col *:gap-2">
      {displayOptions.map(option =>
        option.value === OVERSIZED_VALUE ||
        option.value === MULTIPLE_CALCULATED_VALUE ? (
          <label
            ref={clickOutsideRef}
            key={option.value}
            onClick={() => setDismissed(false)}
            className={cn(
              "flex h-[48px] cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors duration-200 md:justify-start",
              option.value === props.value
                ? "border-[#4AA0A0]"
                : "hover:border-[#00000020]"
            )}
          >
            <div className="flex items-center gap-3">
              <Radio
                value={option.value}
                icon={IconCheck}
                color="#4AA0A0"
                styles={radioStyles}
              />
              <span className="w-max shrink-0 text-[18px] font-bold leading-[24px] text-[#191919]">
                Delivery
              </span>
            </div>
            <Tooltip
              label={option.tooltipText || option.label}
              multiline
              w={240}
              withArrow
              position="top-end"
              arrowOffset={16}
              opened={
                props.isTooltipEnabled &&
                !dismissed &&
                (option.value === props.value || hovered)
              }
            >
              <Badge
                onMouseEnter={() => {
                  setHovered(true);
                  setDismissed(false);
                }}
                onMouseLeave={() => setHovered(false)}
                color="#e8f7f7"
                style={{
                  color: "#191919",
                  cursor: "help",
                  fontSize: "15px",
                  marginInline: isMobile ? "0" : "auto",
                  textTransform: "none",
                }}
                rightSection={<IconInfoCircle size={14} />}
              >
                {option.label}
              </Badge>
            </Tooltip>
          </label>
        ) : (
          <label key={option.value} className="relative">
            <Radio
              value={option.value}
              label={option.label}
              disabled={option.disabled}
              icon={IconCheck}
              color="#4AA0A0"
              className={cn(
                "flex h-[48px] cursor-pointer items-center rounded-lg border p-3 transition-colors duration-200",
                option.value === props.value
                  ? "border-[#4AA0A0]"
                  : "hover:border-[#00000020]"
              )}
              styles={radioStyles}
            />
          </label>
        )
      )}
    </Radio.Group>
  );
};

export default InputRadioGroup;
